import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import useScrollRestoration from './hooks/useScrollRestoration';
import { startProgress, stopProgress } from './utils/progress';
import { Box, Spinner, Center } from '@chakra-ui/react';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { LanguageProvider } from './i18n';

// Layouts
import UserLayout from './layouts/UserLayout';

// Pages - lightweight or first-screen
import LandingPage from './pages/LandingPage';
import RegisterPage from './pages/RegisterPage';
import ProductWorkspaceGate from './components/ProductWorkspaceGate';
import ChooseWorkspace from './pages/ChooseWorkspace';
import EcommerceEntry from './pages/EcommerceEntry';
import LoginPage from './pages/Login';
import PartnerLoginPage from './pages/PartnerLogin';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SiteShell from './pages/SiteShell';
import SiteSubscription from './pages/SiteSubscription';
import { siteRoutePaths } from './pages/siteRoutes';
import Terms from './pages/Terms';
import SettingsPage from './pages/User/Settings';
import ExtensionsPage from './pages/User/Extensions';
import ContactsPage from './pages/User/Contacts';
import CallHistoryPage from './pages/User/CallHistory';
import CallDetailPage from './pages/User/CallDetail';
import CallbackRequestsPage from './pages/User/CallbackRequests';
import ReceiveInfo from './pages/ReceiveInfo';
import RequireFeature from './components/RequireFeature';
import RequireInternalPortalTool from './components/RequireInternalPortalTool';

const EcommerceWorkspace = lazy(() => import('./pages/EcommerceWorkspace'));
// Public website chat (embedded through /chat-widget.js).
const ChatWidget = lazy(() => import('./pages/ChatWidget'));
const ProjectBoard = lazy(() => import('./pages/ProjectBoard'));
const CustomersPage = lazy(() => import('./pages/User/Customers'));
const SenderEmailPage = lazy(() => import('./pages/User/SenderEmail').then((m) => ({ default: m.SenderEmailPage })));
const ChatRecordsPage = lazy(() => import('./pages/User/ChatRecords'));
const ChatRecordDetail = lazy(() => import('./pages/User/ChatRecords').then((m) => ({ default: m.ChatRecordDetail })));

// Heavy pages - lazy loaded to reduce initial bundle
const CalendarPage = lazy(() => import('./pages/User/Calendar'));
const QAPage = lazy(() => import('./pages/User/QA'));
const AITestPage = lazy(() => import('./pages/User/AITest'));
const SMSPage = lazy(() => import('./pages/User/SMSLogs'));

// Site pages with their own URLs (/products, /zh/insights/<id> …), see pages/siteRoutes.js.
const SITE_ROUTE_PATHS = siteRoutePaths();

const PageFallback = () => (
  <Center flex="1" minH="200px">
    <Spinner size="lg" colorScheme="blue" />
  </Center>
);

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated } = useTenant();
  if (!isAuthenticated) {
    const platformReturn = new URLSearchParams(location.search).get('workspace') === 'platform'
      ? `${location.pathname}${location.search}` : undefined;
    return <Navigate to="/login" replace state={{ platformReturn }} />;
  }
  return children;
};

// --- ROUTE DEFINITIONS --- //
export const UserRoutes = () => {
  React.useEffect(() => {
    document.body.classList.add('app-page');
    return () => document.body.classList.remove('app-page');
  }, []);
  return (<Routes>
    <Route path="sender-email" element={<ProtectedRoute><Suspense fallback={<PageFallback />}><SenderEmailPage /></Suspense></ProtectedRoute>} />
    <Route path="ecommerce/*" element={<ProtectedRoute><ProductWorkspaceGate product="ecommerce"><Suspense fallback={<PageFallback />}><EcommerceWorkspace /></Suspense></ProductWorkspaceGate></ProtectedRoute>} />
    <Route path="/" element={
      <ProtectedRoute>
        <ProductWorkspaceGate><UserLayout /></ProductWorkspaceGate>
      </ProtectedRoute>
    }>
      <Route index element={<Navigate to="settings" replace />} />
      <Route path="settings" element={<SettingsPage />} />
      <Route path="extensions" element={<ExtensionsPage />} />
      <Route path="contacts" element={<ContactsPage />} />
      <Route path="products" element={<Navigate to="/app/settings" replace />} />
      <Route path="calendar" element={<RequireFeature feature="booking"><Suspense fallback={<PageFallback />}><CalendarPage /></Suspense></RequireFeature>} />
      <Route path="calendar-new" element={<Navigate to="/app/calendar" replace />} />
      <Route path="qa" element={<RequireFeature feature="qa"><Suspense fallback={<PageFallback />}><QAPage /></Suspense></RequireFeature>} />
      <Route path="ai-test" element={<RequireInternalPortalTool><Suspense fallback={<PageFallback />}><AITestPage /></Suspense></RequireInternalPortalTool>} />
      <Route path="marketplace" element={<Navigate to="/app/settings" replace />} />
      <Route path="sms" element={<RequireFeature feature="sms"><Suspense fallback={<PageFallback />}><SMSPage /></Suspense></RequireFeature>} />
      <Route path="sms-logs" element={<Navigate to="/app/sms" replace />} />
      <Route path="call-history" element={<CallHistoryPage />} />
      <Route path="call-history/:callId" element={<CallDetailPage />} />
      <Route path="chat-records" element={<Suspense fallback={<PageFallback />}><ChatRecordsPage /></Suspense>} />
      <Route path="call-history/chat/:conversationId" element={<Suspense fallback={<PageFallback />}><ChatRecordDetail portalBasePath="/app" backTo="/app/chat-records" /></Suspense>} />
      <Route path="email-config" element={<Navigate to="/app/settings" replace />} />
      <Route path="callback-requests" element={<CallbackRequestsPage />} />
      <Route path="customers" element={<Suspense fallback={<PageFallback />}><CustomersPage /></Suspense>} />
      <Route path="customers/:customerId" element={<Suspense fallback={<PageFallback />}><CustomersPage /></Suspense>} />
      <Route path="*" element={<Navigate to="/app/settings" replace />} />
    </Route>
  </Routes>);
};

// --- MAIN APP COMPONENT --- //
const AppContent = () => {
  useScrollRestoration();

  // Top progress bar on route changes
  React.useEffect(() => {
    startProgress('#1890FF');
    const timer = setTimeout(() => stopProgress(), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box minH="100vh">
      <Routes>
        <Route path="/" element={<SiteShell><LandingPage /></SiteShell>} />
        {SITE_ROUTE_PATHS.map((path) => (
          <Route key={path} path={path} element={<SiteShell><LandingPage /></SiteShell>} />
        ))}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/ecommerce-entry" element={<EcommerceEntry />} />
        <Route path="/choose-workspace" element={<ChooseWorkspace />} />
        <Route path="/partner-login" element={<PartnerLoginPage />} />
        <Route path="/privacy-policy" element={<SiteShell><PrivacyPolicy /></SiteShell>} />
        <Route path="/terms" element={<SiteShell><Terms /></SiteShell>} />
        <Route path="/insights/confirm" element={<SiteShell chat={false}><SiteSubscription mode="confirm" /></SiteShell>} />
        <Route path="/insights/unsubscribe" element={<SiteShell chat={false}><SiteSubscription mode="unsubscribe" /></SiteShell>} />
        <Route path="/chat/:widgetKey" element={<Suspense fallback={null}><ChatWidget /></Suspense>} />
        <Route path="/receive-info" element={<ReceiveInfo />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/app/*" element={<UserRoutes />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );
};

// The project board is reached by its link alone: it sits outside the tenant/login providers so a
// browser holding an expired Aiker session is never bounced to the login page.
const App = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/board/:token" element={<Suspense fallback={null}><ProjectBoard /></Suspense>} />
      <Route path="*" element={(
        <LanguageProvider>
          <TenantProvider>
            <AppContent />
          </TenantProvider>
        </LanguageProvider>
      )} />
    </Routes>
  </BrowserRouter>
);

export default App;
